"use client"; // This directive MUST be at the very top of the file

import { MoreHorizontal, ChevronRight } from "lucide-react"; // Import Lucide icons
import { useTranslations } from "next-intl";
import React, { useState } from "react";

import { GradientOutlineButton } from "@/components/design/gradient-outline-button";
import { Button } from "@/components/ui/button"; // Import shadcn Button component

import { type GeoJSONLineStringFeature } from "./map";
import { JOURNEY_ROUTES, type JourneyRoute } from "./map-data";

interface JourneyOptionProps {
  color: string;
  title: string;
  subtitle: string;
  time: string;
  onClick: (geoJSON: GeoJSONLineStringFeature) => void;
  geoJSON: GeoJSONLineStringFeature;
  isSelected: boolean;
}

const JourneyOption: React.FC<JourneyOptionProps> = ({
  color,
  title,
  subtitle,
  time,
  onClick,
  geoJSON,
  isSelected,
}) => (
  <div
    className={`flex cursor-pointer items-center justify-between rounded-2xl py-2 transition-all duration-100 ${
      isSelected
        ? "bg-[radial-gradient(circle_at_center,hsla(205,20%,50%,0.4)_0%,transparent_100%)]"
        : "hover:bg-[radial-gradient(circle_at_center,hsla(205,20%,50%,0.2)_0%,transparent_100%)]"
    }`}
    onClick={() => onClick(geoJSON)}
  >
    <div className="flex items-center">
      <div className={`mr-6 h-12 w-1 rounded-full ${color}`}></div>
      <div>
        <p className="text-p1 font-semibold">{title}</p>
        <p className="text-p2">{subtitle}</p>
      </div>
    </div>
    <span className="text-p1 font-semibold">{time}</span>
  </div>
);

interface JourneyOptionsProps {
  onRouteSelect: (geoJSON: GeoJSONLineStringFeature) => void;
  selectedRouteGeoJSON: GeoJSONLineStringFeature;
}

function JourneyOptions({
  onRouteSelect,
  selectedRouteGeoJSON,
}: JourneyOptionsProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const t = useTranslations("staticComponents.journeyOptions");

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Determine the URL for the "Wyznacz trasę" button
  // Explicitly cast to string or null to satisfy TypeScript's strictness
  const routeUrl: string | null =
    (selectedRouteGeoJSON?.properties?.url as string) || null;

  return (
    <div
      className={`right-0 top-0 z-10 hidden h-full w-[380px] flex-col justify-between overflow-hidden rounded-[20px] px-4 py-6 text-white md:absolute lg:flex ${
        isCollapsed
          ? "bg-transparent backdrop-blur-0"
          : "bg-[linear-gradient(to_right,#010C15CC_0%,rgba(1,12,21,0.06)_50%,#010C15CC_100%)] backdrop-blur-[22px]"
      }`}
    >
      <div className="flex items-center justify-between">
        {/* Header text content, which will fade out */}
        <div
          className={`flex flex-col transition-opacity duration-300 ease-in-out ${
            isCollapsed ? "pointer-events-none opacity-0" : "opacity-100"
          }`}
        >
          <p className="text-h4">{t("header.title")}</p>
          <p className="text-p opacity-50">{t("header.subtitle")}</p>
        </div>

        {/* The collapse/expand button - always visible */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleCollapse}
          className="ml-auto text-white hover:bg-white/10"
          aria-label={t("toggleButton.ariaLabel")}
        >
          {isCollapsed ? (
            <ChevronRight className="h-6 w-6" />
          ) : (
            <MoreHorizontal className="h-6 w-6" />
          )}
        </Button>
      </div>

      {/* This wrapper div contains all content that should collapse and fade */}
      <div
        className={`flex max-h-[500px] flex-col gap-2 transition-all duration-300 ease-in-out ${isCollapsed ? "pointer-events-none opacity-0" : "opacity-100"}`}
      >
        {/* Route options list */}
        {JOURNEY_ROUTES.map((route: JourneyRoute, index) => (
          <React.Fragment key={index}>
            <div className="ml-1 border-b border-gray-700"></div>
            <JourneyOption
              {...route}
              title={t(`journeyRoutes.${index}.title`)}
              subtitle={t(`journeyRoutes.${index}.subtitle`)}
              onClick={onRouteSelect}
              geoJSON={route.geoJSON}
              isSelected={route.geoJSON === selectedRouteGeoJSON}
            />
          </React.Fragment>
        ))}
        <div className="ml-1 border-b border-gray-700"></div>

        {/* "Wyznacz trasę" button - now a link */}
        <div className="h-12">
          <a
            // Ensure href always receives a string. If routeUrl is null, it uses "#".
            href={routeUrl || "#"}
            target="_blank"
            rel="noopener noreferrer"
            style={{ pointerEvents: routeUrl ? "auto" : "none" }}
            className={routeUrl ? "" : "cursor-not-allowed opacity-50"}
          >
            <GradientOutlineButton disabled={!routeUrl}>
              {t("routeButton.text")}
            </GradientOutlineButton>
          </a>
        </div>
      </div>
    </div>
  );
}

export default JourneyOptions;
